import { createBaseQuery } from '@energy-stacks/shared';
import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { environment } from '@energy-stacks/feature-config';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { tariffsNormalizer } from './normalizers/tariffsNormalizer';
import { TariffDto, TariffsDto } from './tariffsDto';
import { TariffModel, TariffsModel } from './tariffsModel';
import { GetTariffsBody } from './getTariffBody';
import { AddTariffFormData } from './addTariffFormData';
import { mapTariffDataToPayload } from './mapTariffDataToPayload';
import { tariffNormalizer } from './normalizers/tariffNormalizer';
import { EditTariffFormData } from './editTariffFormData';

type EditTariffData = {
  formData: EditTariffFormData;
  tariffId: string;
};

export const tariffsApi = createApi({
  reducerPath: 'brokerTariffsApi',
  tagTypes: ['Tariffs'],
  baseQuery: createBaseQuery(`${environment.ocppServiceUrl}/tariffs`),
  endpoints: (builder) => ({
    getAllTariffs: builder.query<TariffModel[], void>({
      async queryFn(
        _arg,
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ): Promise<QueryReturnValue<TariffModel[], FetchBaseQueryError>> {
        const size = 100;
        const allTariffs: TariffModel[] = [];
        let totalPages = 1;

        const firstRes = await fetchWithBQ({
          url: '/',
          method: 'GET',
          params: { page: 0, size },
        });

        if (firstRes.error)
          return { error: firstRes.error as FetchBaseQueryError };

        const firstData = tariffsNormalizer(firstRes.data as TariffsDto);

        allTariffs.push(...(firstData.tariffs ?? []));
        totalPages = firstData.totalPages ?? 1;

        if (totalPages === 1) {
          return { data: allTariffs };
        }

        const fetches = [];
        for (let i = 1; i < totalPages; i++) {
          fetches.push(
            fetchWithBQ({
              url: '/',
              method: 'GET',
              params: { page: i, size },
            })
          );
        }

        const responses = await Promise.all(fetches);

        for (const res of responses) {
          if (res.error) {
            return { error: res.error as FetchBaseQueryError };
          }
          const data = tariffsNormalizer(res.data as TariffsDto);
          allTariffs.push(...(data.tariffs ?? []));
        }

        return { data: allTariffs };
      },
      providesTags: ['Tariffs'],
    }),

    getTariffs: builder.query<TariffsModel, GetTariffsBody>({
      query: (searchParams) => ({
        url: `/`,
        method: 'GET',
        params: {
          ...searchParams,
        },
      }),
      providesTags: ['Tariffs'],
      transformResponse: (response: TariffsDto) => tariffsNormalizer(response),
    }),

    addTariff: builder.mutation<TariffDto, AddTariffFormData>({
      query: (tariffFormData) => ({
        url: `/`,
        method: 'POST',
        body: mapTariffDataToPayload(tariffFormData),
      }),
      invalidatesTags: ['Tariffs'],
    }),

    editTariff: builder.mutation<void, EditTariffData>({
      query: (body) => ({
        url: `/${body.tariffId}`,
        method: 'PUT',
        body: mapTariffDataToPayload(body.formData),
      }),
      invalidatesTags: ['Tariffs'],
    }),

    getTariff: builder.query<TariffModel, string>({
      query: (tariffId) => `/${tariffId}`,
      keepUnusedDataFor: 0,
      providesTags: (result) => [
        {
          type: 'Tariffs',
          id: result?.id,
        },
      ],
      transformResponse: (response: TariffDto) => tariffNormalizer(response),
    }),
  }),
});

export const {
  useGetAllTariffsQuery,
  useGetTariffsQuery,
  useAddTariffMutation,
  useEditTariffMutation,
  useGetTariffQuery,
} = tariffsApi;
